import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { createBrowserRouter, Navigate, RouterProvider } from 'react-router';
import { QueryClientProvider } from '@tanstack/react-query';
import { ToastProvider } from '@fluent/ui';
import { initSentry } from './lib/sentry.js';
import { AppErrorBoundary } from './lib/AppErrorBoundary.js';
import { initAnalytics } from './lib/analytics.js';
import { initMetaPixel } from './lib/metaPixel.js';
import { queryClient } from './lib/queryClient.js';
import { initNativeDeepLinks } from './native/deepLinks.js';
import { installSilentSwitchFix } from './audio/silentSwitch.js';

// Sentry + PostHog initialize before any route renders. Both are no-ops
// when their keys are unset (dev / preview). Sentry first so it can
// capture any error from analytics init itself. The Meta pixel runs only
// on app.fluent.ge. Deep links are a no-op outside the native (Capacitor)
// wrapper. The silent-switch fix must run before any AudioContext exists.
initSentry();
initAnalytics();
initMetaPixel();
initNativeDeepLinks();
installSilentSwitchFix();
import { AuthProvider } from './auth/AuthProvider.js';
import { ProtectedRoute, PublicRoute } from './auth/ProtectedRoute.js';
import { PendingQueueFlusher } from './progress/PendingQueueFlusher.js';
import { SwUpdateToast } from './pwa/SwUpdateToast.js';
import { InstallPrompt } from './pwa/InstallPrompt.js';
import { InstallController } from './pwa/InstallController.js';
import './pwa/installPromptStore.js'; // registers beforeinstallprompt listener early
import { registerServiceWorker } from './swRegistration.js';
import { Home } from './screens/Home.js';
import { SignUp } from './screens/SignUp.js';
import { Welcome } from './screens/Welcome.js';
import { LogIn } from './screens/LogIn.js';
import { ForgotPassword } from './screens/ForgotPassword.js';
import { ResetPassword } from './screens/ResetPassword.js';
import { CheckEmail } from './screens/CheckEmail.js';
import { Profile } from './screens/Profile.js';
import { Settings } from './screens/Settings.js';
import { Flashcards } from './screens/Flashcards.js';
import { FlashcardSession } from './flashcards/FlashcardSession.js';
import { DeckBrowser } from './flashcards/DeckBrowser.js';
import { FlashcardFlusher } from './flashcards/FlashcardFlusher.js';
import { ExamFlusher, ExamResultFlusher } from './exam/ExamFlusher.js';
import { Achievements } from './screens/Achievements.js';
import { Phrases } from './screens/Phrases.js';
import { ExamHome } from './screens/ExamHome.js';
import { ExamPlayer } from './screens/ExamPlayer.js';
import { Admin } from './screens/Admin.js';
import { ContentReview } from './screens/ContentReview.js';
import { AudioReview } from './screens/AudioReview.js';
import { ExamAudioReview } from './screens/ExamAudioReview.js';
import { AudioRecord } from './screens/AudioRecord.js';
import { AuthDebug } from './screens/AuthDebug.js';
import { GoogleCallback } from './screens/GoogleCallback.js';
import { Onboarding } from './screens/Onboarding.js';
import { LessonPlayer } from './lesson/LessonPlayer.js';
import { PilotBReview } from './PilotBReview.js';
import { PilotCReview } from './PilotCReview.js';
import { Cluster1Review } from './Cluster1Review.js';
import { Cluster2Review } from './Cluster2Review.js';
import { Cluster3Review } from './Cluster3Review.js';
import { Cluster4Review } from './Cluster4Review.js';
import { Cluster5Review } from './Cluster5Review.js';
import { Cluster6Review } from './Cluster6Review.js';
import { VoicePilotReview } from './VoicePilotReview.js';
import { VoicePilotDialogueReview } from './VoicePilotDialogueReview.js';
import { AdminReview } from './AdminReview.js';
import { SkillTreeReview } from './SkillTreeReview.js';
import { LessonReview, LessonReviewPlayer } from './LessonReview.js';
import { AwardsReview } from './AwardsReview.js';
import { ProfileReview } from './ProfileReview.js';
import { SettingsReview } from './SettingsReview.js';
import { GrammarVideoReview } from './GrammarVideoReview.js';
import { SubscriptionReview } from './SubscriptionReview.js';
import { Subscription } from './screens/Subscription.js';
import { MissionsReview } from './MissionsReview.js';
import { PhrasesReview } from './PhrasesReview.js';
import '@fluent/tokens/index.css';
import './lib/i18n.js'; // eager-load so useTranslation works on first render
import './styles.css';

const router = createBrowserRouter([
  // Signed-in surfaces
  { path: '/', element: <ProtectedRoute><Home /></ProtectedRoute> },
  { path: '/profile', element: <ProtectedRoute><Profile /></ProtectedRoute> },
  { path: '/settings', element: <ProtectedRoute><Settings /></ProtectedRoute> },
  { path: '/subscription', element: <ProtectedRoute><Subscription /></ProtectedRoute> },
  // The missions hub lives at /achievements (opened from the Home missions
  // card and Profile since ფრაზები took its tab) — keep the deep link alive.
  { path: '/missions', element: <Navigate to="/achievements" replace /> },
  // ფრაზები — the phrases course A1–C2 (2026-10-18, PHRASES.md).
  { path: '/phrases', element: <ProtectedRoute><Phrases /></ProtectedRoute> },
  { path: '/flashcards', element: <ProtectedRoute><Flashcards /></ProtectedRoute> },
  { path: '/flashcards/session', element: <ProtectedRoute><FlashcardSession /></ProtectedRoute> },
  { path: '/flashcards/browse', element: <ProtectedRoute><DeckBrowser /></ProtectedRoute> },
  // ტესტი — the national-exam simulator replaced Practice (Tornike 2026-09-25).
  { path: '/test', element: <ProtectedRoute><ExamHome /></ProtectedRoute> },
  { path: '/test/:testId', element: <ProtectedRoute><ExamPlayer /></ProtectedRoute> },
  { path: '/practice', element: <Navigate to="/test" replace /> },
  { path: '/practice/session', element: <Navigate to="/test" replace /> },
  // Old tab destinations — keep deep links alive.
  { path: '/vocab', element: <Navigate to="/flashcards/browse" replace /> },
  { path: '/podcasts', element: <Navigate to="/test" replace /> },
  { path: '/achievements', element: <ProtectedRoute><Achievements /></ProtectedRoute> },
  { path: '/learn/:lessonId', element: <ProtectedRoute><LessonPlayer /></ProtectedRoute> },
  { path: '/onboarding', element: <ProtectedRoute skipOnboardingGate><Onboarding /></ProtectedRoute> },
  { path: '/admin', element: <ProtectedRoute skipOnboardingGate><Admin /></ProtectedRoute> },
  { path: '/review', element: <ProtectedRoute skipOnboardingGate><ContentReview /></ProtectedRoute> },
  { path: '/audio-review', element: <AudioReview /> },
  { path: '/exam-audio-review', element: <ExamAudioReview /> },
  { path: '/audio-record', element: <AudioRecord /> },

  // Auth surfaces — redirect home when already signed in
  { path: '/welcome', element: <PublicRoute><Welcome /></PublicRoute> },
  { path: '/signup', element: <PublicRoute><SignUp /></PublicRoute> },
  { path: '/login', element: <PublicRoute><LogIn /></PublicRoute> },
  { path: '/forgot-password', element: <PublicRoute><ForgotPassword /></PublicRoute> },
  { path: '/reset-password', element: <ResetPassword /> },
  { path: '/check-email', element: <CheckEmail /> },
  { path: '/auth/debug', element: <AuthDebug /> },
  { path: '/auth/google', element: <GoogleCallback /> },

  // Design-system review routes — stay accessible without auth during Phase 1 QA
  { path: '/pilot-b', element: <PilotBReview /> },
  { path: '/pilot-c', element: <PilotCReview /> },
  { path: '/cluster-1', element: <Cluster1Review /> },
  { path: '/cluster-2', element: <Cluster2Review /> },
  { path: '/cluster-3', element: <Cluster3Review /> },
  { path: '/cluster-4', element: <Cluster4Review /> },
  { path: '/cluster-5', element: <Cluster5Review /> },
  { path: '/cluster-6', element: <Cluster6Review /> },
  { path: '/voice-pilot', element: <VoicePilotReview /> },
  { path: '/voice-pilot-dialogue', element: <VoicePilotDialogueReview /> },
  { path: '/admin-review', element: <AdminReview /> },
  { path: '/skill-tree-review', element: <SkillTreeReview /> },
  // Preview hosts only (LessonReview.tsx): play any lesson with no sign-in, for content checks.
  { path: '/lesson-review', element: <LessonReview /> },
  { path: '/lesson-review/:lessonId', element: <LessonReviewPlayer /> },
  { path: '/awards-review', element: <AwardsReview /> },
  { path: '/profile-review', element: <ProfileReview /> },
  { path: '/settings-review', element: <SettingsReview /> },
  { path: '/grammar-video-review', element: <GrammarVideoReview /> },
  { path: '/subscription-review', element: <SubscriptionReview /> },
  { path: '/missions-review', element: <MissionsReview /> },
  { path: '/test-review', element: <ExamHome demo basePath="/test-review" /> },
  { path: '/phrases-review', element: <PhrasesReview /> },
  { path: '/test-review/:testId', element: <ExamPlayer demo basePath="/test-review" /> },
]);

const rootEl = document.getElementById('root');
if (!rootEl) {
  throw new Error('Root element #root not found');
}

createRoot(rootEl).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <ToastProvider>
          <PendingQueueFlusher />
          <FlashcardFlusher />
          <ExamFlusher />
          <ExamResultFlusher />
          <SwUpdateToast />
          <InstallPrompt />
          <InstallController />
          <AppErrorBoundary>
            <RouterProvider router={router} />
          </AppErrorBoundary>
        </ToastProvider>
      </AuthProvider>
    </QueryClientProvider>
  </StrictMode>,
);

// Service worker registration — production only (vite-plugin-pwa skips in dev).
registerServiceWorker();
